import EolIcon from "@/components/icons/chapters/eolIcon";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import NoImpactBanner from "@/components/NoImpactBanner/NoImpactBanner";
import List from "@/components/visualizations/List/List";
import Widget from "@/pages/dashboard/components/Widget/Widget";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { useDashboardOrderCounts } from "@/pages/dashboard/hooks/usePoSo";
import { ProductBy, useProductEolImpact } from "@/pages/dashboard/hooks/useProductImpact";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import Icon from "@ant-design/icons";
import { Link } from "react-router-dom";

function ProductEolImpact({
  metric,
  productId,
  by,
}: {
  metric: ImpactMetric;
  productId: number;
  by: ProductBy;
}) {
  const { data, isLoading, isFetching } = useProductEolImpact({
    productId,
    by,
    metric: metric.key,
  });

  const { data: orderCounts } = useDashboardOrderCounts({ productId });
  const { year } = useDashboardYear();

  if (orderCounts?.po === 0) {
    return (
      <NoImpactBanner title={`No end-of-life impact for this product in ${year}`}>
        <span>
          This product was not found in any <strong>purchase orders</strong> in {year}, so no impact
          could be calculated. Navigate to a different year, or upload{" "}
          <Link to="/orders/purchase_orders">purchase orders</Link>.
        </span>
      </NoImpactBanner>
    );
  }

  if (data?.name === null) {
    return (
      <NoImpactBanner
        title={`No end-of-life impact`}
        size="small"
      >
        <span>
          Check if you've uploaded data about the{" "}
          <Link to={`/products/${productId}/eol`}>end-of-life</Link> phase of this product.
        </span>
      </NoImpactBanner>
    );
  }

  const listData =
    data && data.name !== null
      ? [
          {
            icon: <Icon component={EolIcon} />,
            label: <CrossReference to={`/products/${productId}/eol`}>{data.name}</CrossReference>,
            value: data.impact[metric.key],
          },
        ]
      : [];

  return (
    <Widget title={"End of life impact"}>
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}

export default ProductEolImpact;
